import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/constants";
import { HeartHandshake, ExternalLink, Loader2, PenLine, Newspaper, ArrowRight } from "lucide-react";

// ITEM 19 — Nurture. A REASON to reach out now, never a cadence. Rex only shows people it can
// give a genuine, current reason for. No reason, no card. Each reason carries its source.
const REASON_META = {
  captured_next_step: { icon: PenLine, chip: "You planned this", tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  sector_signal: { icon: Newspaper, chip: "Something moved", tone: "text-indigo-700 bg-indigo-50 border-indigo-200" },
};

const daysLabel = (d) => (d == null ? "no contact on record" : d === 0 ? "spoke today" : d === 1 ? "1 day since contact" : `${d} days since contact`);

export default function Nurture() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/relationship/nurture?limit=25`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-6 py-8" data-testid="nurture-page">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-lg bg-rose-50 p-2 text-rose-600"><HeartHandshake className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Nurture</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            The few people worth reaching <span className="font-medium text-slate-700">now</span> — each with a real,
            current reason. This is not a list of who you haven't called; a name only appears when Rex can tell you
            <span className="font-medium text-slate-700"> why now</span>.
          </p>
        </div>
      </div>

      {loading && (
        <div className="mt-10 flex items-center gap-2 text-sm text-slate-400" data-testid="nurture-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Looking for genuine reasons…
        </div>
      )}

      {!loading && data && (
        <>
          <div className="mt-5 flex items-center gap-4 text-xs text-slate-500" data-testid="nurture-summary">
            <span><span className="font-semibold text-slate-900">{data.with_reason}</span> worth reaching now</span>
            <span className="text-slate-300">•</span>
            <span>{data.considered} relationships considered</span>
          </div>

          {data.note && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-500" data-testid="nurture-note">
              {data.note}
            </div>
          )}

          {data.items.length === 0 ? (
            <div className="mt-8 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center" data-testid="nurture-empty">
              <p className="text-sm font-medium text-slate-700">Nobody with a genuine reason right now.</p>
              <p className="mt-1 text-sm text-slate-500">That's the point — Rex won't invent a reason to make you call people. Check back when something moves.</p>
            </div>
          ) : (
            <div className="mt-6 space-y-3" data-testid="nurture-list">
              {data.items.map((it) => {
                const meta = REASON_META[it.reason_type] || REASON_META.sector_signal;
                const Icon = meta.icon;
                const url = it.provenance?.source_url;
                return (
                  <div key={it.client_id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`nurture-item-${it.client_id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Link to={`/clients/${it.client_id}`} className="text-base font-semibold text-slate-900 hover:underline" data-testid="nurture-client-link">
                          {it.company_name}
                        </Link>
                        {it.who && (
                          <span className="text-sm text-slate-500">
                            → {it.who.name}{it.who.title ? <span className="text-slate-400"> · {it.who.title}</span> : null}
                          </span>
                        )}
                      </div>
                      <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium ${meta.tone}`} data-testid="nurture-reason-chip">
                        <Icon className="mr-1 inline h-3 w-3" />{meta.chip}
                      </span>
                    </div>

                    <p className="mt-2 text-sm text-slate-700" data-testid="nurture-reason">{it.reason}</p>

                    <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
                      <span>{daysLabel(it.days_since_contact)}</span>
                      {it.matters?.why?.length > 0 && (
                        <>
                          <span className="text-slate-300">•</span>
                          <span>{it.matters.why.join(" · ")}</span>
                        </>
                      )}
                      {url && (
                        <a href={url} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-indigo-600 hover:underline" data-testid="nurture-source-link">
                          Source <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      <Link to={`/clients/${it.client_id}`} className={`inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 ${url ? "" : "ml-auto"}`} data-testid="nurture-open">
                        Open <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
